import React, { useEffect, useState } from 'react'
// Reusable font styles
const camptonFontStyle = { fontFamily: 'Campton' }
const campton14Style = { fontFamily: 'Campton', fontSize: '14px' }
const campton22Style = { fontFamily: 'Campton', fontSize: '22px', fontWeight: 'bold' }
import Header from './Header'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { useTranslation } from 'react-i18next'
import { actavationFromFunction, actavationFromSubmit } from '../state/actions'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles({
  languageSelector: {
    minWidth: '120px',
    margin: 0,
    position: 'absolute',
    top: '180px',
    right: '100px'
  }
})
enum ActivationStatus {
  Valid = 'Valid',
  Invalid = 'Invalid'
}

enum ErrorMessage {
  LinkExpired = 'Link has expired',
  OrderAlreadyPlaced = 'Order already placed. Link has expired!'
}
interface SignUpData {
  CustomerEosId: string
  AppId: number
  OrderDate: string
  SalesRepEmail: string
  CustomerName: string
  CustomerStreet: string
  CustomerCity: string
  CustomerZipCode: string
  CustomerEmail: string
  CustomerNumber: string
  ShipToLocation: string
  ModuleName: string
  ModulePrice: number
}

interface ActivationResponse {
  Data: {
    Status: ActivationStatus
    Message: string
    SignUpData: SignUpData
  }
}

const ActivationFrom: React.FC = () => {
  const classes = useStyles()
  const [checked, setChecked] = useState({
    privacyPolicy: false,
    termsOfUse: false,
    dataProcessing: false
  })
  const [orderPlaced, setOrderPlaced] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [loading, setLoading] = useState(true)
  const { i18n, t } = useTranslation()
  const [selectedLang, setSelectedLang] = useState<string>('de-DE')
  const allChecked = Object.values(checked).every(Boolean)
  const [signUpData, setSignUpData] = useState({} as SignUpData)
  const docLinksPV = {
    privacyPolicy: 'https://media.carparts-cat.com/webdata1/rdimages/492/2026-02-25_Datenschutzinformation_PV_KOMPASS_4.0.pdf',
    termsOfUse: 'https://media.carparts-cat.com/webdata1/rdimages/492/2023-08-29_NuL_PV_KOMPASS_40.pdf',
    dataProcessing: 'https://media.carparts-cat.com/webdata1/rdimages/492/2023-08-29_AVV_PV_KOMPASS_40.pdf'
  }
  const docLinksSTA = {
    privacyPolicy: 'https://media.carparts-cat.com/webdata1/rdimages/477/2026-02-25-Datenschutzinformation-STAkis-4.0.pdf',
    termsOfUse: 'https://media.carparts-cat.com/webdata1/rdimages/477/2023-08-29_NuL_STAkis_40_DE.pdf',
    dataProcessing: 'https://media.carparts-cat.com/webdata1/rdimages/477/2023-08-29_AVV_STAkis_40_DE.pdf'
  }
  const docLinks = signUpData.AppId === 2 ? docLinksSTA : docLinksPV
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked({ ...checked, [event.target.name]: event.target.checked })
  }
  const dispatch = useDispatch()

  const supportURL = useSelector((state: IRootReducerState) => state.auth?.urls?.support)

  const handleLangChange = (event: SelectChangeEvent<string>) => {
    const newLang = event.target.value
    setSelectedLang(newLang)
    i18n.changeLanguage(newLang)
  }

  useEffect(() => {
    i18n.changeLanguage(selectedLang)
  }, [])

  useEffect(() => {
    const getActivationStatus = async () => {
      const currentUrl = window.location.href
      const hrefCode = currentUrl.split('/').pop()
      setLoading(true)
      try {
        const activationStatus = (await dispatch(actavationFromFunction(hrefCode ?? ''))) as unknown as ActivationResponse
        setLoading(false)
        if (activationStatus.Data.Status === ActivationStatus.Valid) {
          setSignUpData(activationStatus.Data.SignUpData)
          setIsValid(true)
        } else if (activationStatus.Data.Status === ActivationStatus.Invalid) {
          const message = activationStatus.Data.Message
          if (message === ErrorMessage.LinkExpired || message === ErrorMessage.OrderAlreadyPlaced) {
            setErrorMessage(t('ActivationPage.linkExpiredmsg'))
          } else {
            setErrorMessage(message)
          }
        }
      } catch (error) {
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }
    getActivationStatus()
  }, [supportURL, t])

  const handleButtonClick = async () => {
    const currentUrl = window.location.href
    const hrefCode = currentUrl.split('/').pop()
    setLoading(true)
    try {
      const activationStatus = await dispatch(actavationFromSubmit(hrefCode ?? ''))
      setLoading(false)
      if ((activationStatus as any).Data.Status === 'success') {
        setOrderPlaced(true)
      } else {
        setErrorMessage(t('ActivationPage.orderFailed'))
      }
    } catch (error) {
      setLoading(false)
      setErrorMessage(t('ActivationPage.connectionError'))
    } finally {
      setLoading(false)
    }
  }
  const languageSelector = (
    <Box
      sx={{
        marginBottom: 5,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        backgroundColor: (theme: { palette: { common: { black: any } } }) => theme.palette.common.black,
        marginX: '17.5%',
        ...camptonFontStyle
      }}
    >
      <div className='language-selector'>
        <FormControl variant='outlined' className={classes.languageSelector} size='small'>
          <InputLabel sx={campton14Style} id='demo-simple-select-standard-label' data-testid='select-language-label'>
            Select Language
          </InputLabel>
          <Select
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            label='Select language'
            size='small'
            value={selectedLang}
            onChange={handleLangChange}
            sx={campton14Style}
          >
            <MenuItem value='en-GB'>English</MenuItem>
            <MenuItem value='nl-NL'>Dutch</MenuItem>
            <MenuItem value='fr-FR'>French</MenuItem>
            <MenuItem value='de-DE'>German</MenuItem>
            <MenuItem value='it-IT'>Italian</MenuItem>
          </Select>
        </FormControl>
      </div>
      <Typography
        variant='subtitle1'
        sx={{
          color: '#555555',
          fontWeight: 400,
          fontSize: '14px'
        }}
      />
    </Box>
  )

  const loader = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
      <CircularProgress size={20} />
    </Box>
  )

  const orderPlacedBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        minHeight: 'calc(100vh - 190px)',
        ...camptonFontStyle
      }}
    >
      <Typography variant='body1' sx={campton22Style} data-testid='order-success-heading'>
        {`${t('ActivationPage.orderSuccessHeading')}`} <br />
        <br />
      </Typography>
      <Typography variant='body1' sx={campton14Style}>
        {`${t('ActivationPage.orderSuccessMessagePart1')} `}
        {signUpData?.ModuleName} {` ${t('ActivationPage.orderSuccessMessagePart2')}:`}
        <br />
        {`${t('ActivationPage.orderSuccessMessagePart3')}: ${signUpData?.CustomerEmail}`}
      </Typography>
    </Box>
  )

  const errorMessageBox = (
    <Box
      sx={{
        width: '65%',
        height: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        borderRadius: 2,
        ...camptonFontStyle
      }}
    >
      <ErrorOutline sx={{ fontSize: 40, color: 'red' }} data-testid='error-icon' />
      <Typography variant='subtitle1' sx={{ mt: 2, ...campton14Style }}>
        {errorMessage}
      </Typography>
    </Box>
  )

  const isValidBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        minHeight: 'calc(100vh - 190px)',
        ...camptonFontStyle
      }}
    >
      <Typography variant='body1' sx={campton14Style}>
        {/* ...existing code... */}
        <br />
        <br />
        {`${t('ActivationPage.greetings')},`} <br />
        <br />
        {`${t('ActivationPage.activationFormInfoStart')} ${signUpData?.ModuleName} ${t('ActivationPage.activationFormInfoEnd')} `} <br />
        {`${t('ActivationPage.activationFormInfoSecondPara')}`}
        <br />
        <br />
        <Table
          sx={{
            marginBottom: 2,
            width: '100%',
            border: 'none',
            '& td, & th': { padding: '4px 0px', border: 'none', ...campton14Style },
            '& tr': { border: 'none' }
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', ...campton14Style }}>{t('ActivationPage.orderDate')}</TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', ...campton14Style }}>{signUpData?.OrderDate}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.salesRepEmail')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.SalesRepEmail}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerName')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.CustomerName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerStreet')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.CustomerStreet}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerZipCity')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.CustomerZipCode} {signUpData?.CustomerCity}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerEmail')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.CustomerEmail}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerNumber')}
              </TableCell>

              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.CustomerNumber}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.customerShipToLocation')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.ShipToLocation}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {t('ActivationPage.basePackagePrice')}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '14px', fontFamily: 'Campton' }}>
                {signUpData?.ModulePrice}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked.privacyPolicy}
            onChange={handleCheckboxChange}
            name='privacyPolicy'
            sx={{ paddingTop: 0, paddingBottom: 0 }}
          />
        }
        label={
          <Typography sx={{ marginBottom: 0, ...campton14Style }}>
            {`${t('ActivationPage.ihave')} `}
            <Link href={docLinks.termsOfUse} target='_blank'>
              {`${t('ActivationPage.readAndAgree')} `}
            </Link>
            {`${t('ActivationPage.totheprivacypolicy')} `}
            <Typography component='span' sx={{ color: 'red', ...campton14Style }}>
              *
            </Typography>
          </Typography>
        }
        sx={{ marginBottom: 1 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={checked.termsOfUse}
            onChange={handleCheckboxChange}
            name='termsOfUse'
            sx={{ paddingTop: 0, paddingBottom: 0 }}
          />
        }
        label={
          <Typography component='span' sx={{ marginBottom: 0, ...campton14Style }}>
            {`${t('ActivationPage.ihave')} `}
            <Link href={docLinks.privacyPolicy} target='_blank'>
              {`${t('ActivationPage.readAndAgree')} `}
            </Link>
            {`${t('ActivationPage.tothetermsofuseandlicense')} `}
            <Typography component='span' sx={{ color: 'red', ...campton14Style }}>
              *
            </Typography>
          </Typography>
        }
        sx={{ marginBottom: 1, fontFamily: 'Campton' }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={checked.dataProcessing}
            onChange={handleCheckboxChange}
            name='dataProcessing'
            sx={{ paddingTop: 0, paddingBottom: 0 }}
          />
        }
        label={
          <Typography component='span' sx={{ marginBottom: 0, ...campton14Style }}>
            {`${t('ActivationPage.ihave')} `}
            <Link href={docLinks.dataProcessing} target='_blank'>
              {t('ActivationPage.readthedataprocessingagreement')}
            </Link>{' '}
            {t('ActivationPage.andAgreetoIt')}
            <Typography component='span' sx={{ color: 'red', ...campton14Style }}>
              *
            </Typography>
          </Typography>
        }
        sx={{ marginBottom: 1, fontFamily: 'Campton' }}
      />

      <Typography component='span' sx={campton14Style}>
        <Typography component='span' sx={{ color: 'red', ...campton14Style }}>
          *
        </Typography>
        {`: ${t('ActivationPage.infoBottom')}`}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          marginBottom: 2,
          ...camptonFontStyle
        }}
      >
        <Button
          variant='contained'
          onClick={handleButtonClick}
          disabled={!allChecked}
          data-testid='place-order'
          sx={{ backgroundColor: '#5F9AFF', ...campton14Style }}
        >
          {t('ActivationPage.placeOrder')}
        </Button>
      </Box>
    </Box>
  )

  return (
    <>
      <Header />

      <Paper
        sx={{
          width: '100%',
          flexDirection: 'column',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          overflow: 'auto',
          margin: '0 auto',
          padding: '0 32px',
          minHeight: 'calc(100vh - 150px)',
          ...camptonFontStyle
        }}
      >
        {languageSelector}
        {(() => {
          switch (true) {
            case loading:
              return loader
            case orderPlaced:
              return orderPlacedBlock
            case !!errorMessage:
              return errorMessageBox
            case isValid:
              return isValidBlock
            default:
              return null
          }
        })()}
      </Paper>
    </>
  )
}

export default ActivationFrom
